import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Users as UsersIcon, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime } from "@/lib/format";
import type { User } from "@/types/auth";
import { useDebounce } from "@/hooks/use-debounce";

export function UsersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);

  // 🛠️ FIXED: Keep track of which specific user is being updated
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", debounced],
    queryFn: () => get<User[]>("/admin/users", debounced ? { q: debounced } : {}),
  });

  const changeStatus = useMutation({
    mutationFn: ({ userId, newStatus }: { userId: string, newStatus: string }) => 
      post(`/admin/users/${userId}/status`, { status: newStatus }),
    onSuccess: () => {
      toast.success("User status updated successfully");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onSettled: () => {
      // 🛠️ Clear the loading state regardless of success or failure
      setUpdatingUserId(null);
    }
  });

  const handleStatusChange = (userId: string, newStatus: string) => {
    setUpdatingUserId(userId);
    changeStatus.mutate({ userId, newStatus });
  };

  const users = useMemo(() => data ?? [], [data]);

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Users</h1>
        <p className="text-sm text-surface-500 mt-1">
          Search and manage customer accounts. Blocked users cannot log in.
        </p>
      </header>

      <Card>
        <div className="p-5 border-b border-surface-200">
          <Input
            placeholder="Search by User ID, name, mobile, or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : users.length === 0 ? (
            <EmptyState icon={UsersIcon} title="No users found" />
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[800px]">
                <THead>
                  <TR>
                    <TH>User ID</TH>
                    <TH>Name</TH>
                    <TH>Mobile</TH>
                    <TH>Role</TH>
                    <TH>Status</TH>
                    <TH>Entries</TH>
                    <TH>Registered</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <tbody>
                  {users.map((u) => {
                    const isBlocked = u.status === "locked" || u.status === "inactive";
                    // 🛠️ Check if THIS specific button should be loading
                    const isThisButtonLoading = changeStatus.isPending && updatingUserId === u.user_id;

                    return (
                      <TR key={u.user_id}>
                        <TD className="font-mono text-xs">
                          <Link to={`/admin/users/${u.user_id}`}
                                className="text-brand-600 font-bold hover:text-brand-700">
                            {u.user_id}
                          </Link>
                        </TD>
                        <TD className="font-medium text-surface-900">{u.full_name}</TD>
                        <TD className="font-mono text-xs">{u.mobile}</TD>
                        <TD>
                          <Badge tone={u.role === "customer" ? "neutral" : "info"} className="capitalize">
                            {u.role.replace(/_/g, " ")}
                          </Badge>
                        </TD>
                        <TD><StatusPill status={u.status} /></TD>
                        <TD className="font-bold">{u.club_entry_count}</TD>
                        <TD className="text-[11px] text-surface-500 whitespace-nowrap">{formatDateTime(u.created_at)}</TD>
                        <TD className="text-right">
                          {/* 🛠️ FIXED: Loading state logic */}
                          {isBlocked ? (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              loading={isThisButtonLoading}
                              disabled={changeStatus.isPending && !isThisButtonLoading} // Disable other buttons while one is loading
                              leftIcon={<CheckCircle2 className="h-3.5 w-3.5 text-success-600" />}
                              onClick={() => handleStatusChange(u.user_id, "active")}
                            >
                              Unblock
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="danger" 
                              loading={isThisButtonLoading}
                              disabled={changeStatus.isPending && !isThisButtonLoading}
                              leftIcon={<ShieldAlert className="h-3.5 w-3.5" />}
                              onClick={() => handleStatusChange(u.user_id, "locked")}
                            >
                              Block
                            </Button>
                          )}
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}